import {Button} from '@dropbox/dig-components/buttons';
import {Checkbox} from '@dropbox/dig-components/controls';
import {FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {TextArea, TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {pulseUserAtom} from 'atoms/auth';
import {
  type AdminContextEngineChatBody,
  AdminContextEngineService,
  ApiError,
  OpenAPI,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {
  conversationIdFromCeResponse,
  extractCeSourcesFromRaw,
  lastAssistantTextFromCeResponse,
} from 'helpers/contextEngineRawResponse';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useTaskPolling} from 'hooks/useTaskPolling';
import {useAtomValue} from 'jotai';
import {useCallback, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';
import {
  ContextEngineAssistantTextWithCitationChips,
  ContextEngineSourcesMenuButton,
  contextEngineSourcesToByIndexMap,
} from 'views/workstreams/WorkstreamChatSourceChips';
import {tryParseStructuredDraftFromCeResponse} from 'views/workstreams/workstreamContextEngineChatApi';

const ADMIN_CONTEXT_ENGINE_SOURCE = 'company_os_admin_test';
const ADMIN_CE_MAX_ATTACHMENTS = 20;

/** Serialized ``user_message.agent_payload`` for CompanyOS CE agents. */
function buildCompanyosAgentPayloadJson(options: {
  enableNativeWriteTools: boolean;
  workstreamId?: number;
}): string | undefined {
  const payload: Record<string, unknown> = {};
  if (options.enableNativeWriteTools) {
    payload.enable_native_write_tools = true;
  }
  if (options.workstreamId != null && Number.isFinite(options.workstreamId)) {
    payload.workstream_id = options.workstreamId;
  }
  return Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined;
}

function rawCeDraftFromResponse(raw: Record<string, unknown>): Record<string, unknown> | null {
  const direct = raw.draft;
  if (direct && typeof direct === 'object') {
    return direct as Record<string, unknown>;
  }
  const nested = raw.raw;
  if (nested && typeof nested === 'object') {
    const draft = (nested as Record<string, unknown>).draft;
    if (draft && typeof draft === 'object') {
      return draft as Record<string, unknown>;
    }
  }
  return null;
}

/** Dropbox get_response body with stable key order (matches client.get_response assembly). */
type SearchParamsTag = '' | 'no_search' | 'dash_search' | 'web_search';
type ToolsOrchestration = '' | 'default' | 'enable' | 'disable';

function buildAdminCeGetResponsePayload(
  message: string,
  conversationId: string,
  source: string,
  options: {
    userAttachments: Array<{url: string; title: string}>;
    userMessageAgentIds?: string[];
    modelChoice?: string;
    enableAdvancedCitations: boolean;
    enableNativeWriteTools: boolean;
    workstreamIdForAgent?: number;
    searchParams: SearchParamsTag;
    toolsOrchestration: ToolsOrchestration;
    skipPersistence: boolean;
    excludeHistory: boolean;
    disableDiagnostics: boolean;
  }
): Record<string, unknown> {
  const userMessage: Record<string, unknown> = {text: message.trim()};
  const agentPayload = buildCompanyosAgentPayloadJson({
    enableNativeWriteTools: options.enableNativeWriteTools,
    workstreamId: options.workstreamIdForAgent,
  });
  if (agentPayload) {
    userMessage.agent_payload = agentPayload;
  }
  if (options.userAttachments.length > 0) {
    userMessage.attachments = options.userAttachments.map((a) =>
      a.title ? {url: a.url, title: a.title} : {url: a.url}
    );
  }
  const agentIds = options.userMessageAgentIds;
  if (agentIds && agentIds.length > 0) {
    userMessage.agent_ids = agentIds;
  }

  const payload: Record<string, unknown> = {
    conversation_id: conversationId.trim(),
    user_message: userMessage,
    source,
  };
  const mc = options.modelChoice?.trim();
  if (mc) {
    payload.model_choice = mc;
  }
  if (options.enableAdvancedCitations) {
    payload.enable_advanced_citations = true;
  }
  if (options.searchParams) {
    payload.search_params = {search_target: {'.tag': options.searchParams}};
  }
  if (options.toolsOrchestration) {
    payload.tools_orchestration = {'.tag': options.toolsOrchestration};
  }
  if (options.skipPersistence) {
    payload.skip_persistence = true;
  }
  if (options.excludeHistory) {
    payload.exclude_history = true;
  }
  if (options.disableDiagnostics) {
    payload.debug_enabled = false;
    payload.diagnostics_config = {
      collect_service_logs: false,
      enable_llm_judge: false,
    };
    payload.flair_capabilities = [];
  }
  return payload;
}

export const AdminContextEngine = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();

  const [message, setMessage] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [attachmentRows, setAttachmentRows] = useState<
    Array<{id: number; url: string; title: string}>
  >([]);
  const nextAttachmentId = useRef(0);
  const [userMessageAgentIdsCsv, setUserMessageAgentIdsCsv] = useState(
    'companyos_workstream_update'
  );
  const [enableAdvancedCitations, setEnableAdvancedCitations] = useState(true);
  const [enableNativeWriteTools, setEnableNativeWriteTools] = useState(false);
  const [workstreamIdForAgent, setWorkstreamIdForAgent] = useState('');
  const [modelChoice, setModelChoice] = useState('');
  const [searchParams, setSearchParams] = useState<SearchParamsTag>('');
  const [toolsOrchestration, setToolsOrchestration] = useState<ToolsOrchestration>('');
  const [skipPersistence, setSkipPersistence] = useState(false);
  const [excludeHistory, setExcludeHistory] = useState(false);
  const [disableDiagnostics, setDisableDiagnostics] = useState(false);
  const [skipCelery, setSkipCelery] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverElapsedMs, setServerElapsedMs] = useState<number | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<Record<string, unknown> | null>(null);
  const [clientRoundTripMs, setClientRoundTripMs] = useState<number | null>(null);
  const roundTripStartRef = useRef<number | null>(null);

  const startAdminCeTask = useCallback(async (payload: Record<string, unknown>) => {
    return getService(
      AdminContextEngineService
    ).adminContextEngineChatApiV1AdminContextEngineChatPost(payload as AdminContextEngineChatBody);
  }, []);

  const getAdminCeTaskStatus = useCallback(async (taskId: string) => {
    try {
      const r =
        await getService(
          AdminContextEngineService
        ).adminContextEngineChatStatusApiV1AdminContextEngineChatTaskIdGet(taskId);
      if (r.status === 'processing') {
        return {status: 'processing' as const};
      }
      return {status: 'completed' as const, result: r.result};
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? `${e.status}: ${
              typeof e.body === 'object' && e.body && 'detail' in e.body
                ? String((e.body as {detail?: unknown}).detail)
                : e.message
            }`
          : e instanceof Error
            ? e.message
            : String(e);
      setErrorText(msg);
      throw e;
    }
  }, []);

  const {start: startAdminCePoll} = useTaskPolling<
    Record<string, unknown>,
    [Record<string, unknown>]
  >({
    queryKeyPrefix: ['adminContextEngineChat'],
    startTask: startAdminCeTask,
    getStatus: getAdminCeTaskStatus,
    refetchInterval: 1000,
    onCompleted: (result) => {
      setLastResponse(result);
      const nextConv = conversationIdFromCeResponse(result);
      if (nextConv) {
        setConversationId(nextConv);
      }
      setIsSubmitting(false);
      if (roundTripStartRef.current != null) {
        setClientRoundTripMs(Math.round(performance.now() - roundTripStartRef.current));
        roundTripStartRef.current = null;
      }
    },
    onFailed: () => {
      setIsSubmitting(false);
      if (roundTripStartRef.current != null) {
        setClientRoundTripMs(Math.round(performance.now() - roundTripStartRef.current));
        roundTripStartRef.current = null;
      }
    },
  });

  useDocumentTitle('Context engine chat');

  const canSubmit = useMemo(
    () => message.trim().length > 0 && !isSubmitting,
    [message, isSubmitting]
  );

  const ceRaw = useMemo((): Record<string, unknown> | null => {
    if (!lastResponse) {
      return null;
    }
    const nested = lastResponse.raw;
    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }
    return lastResponse;
  }, [lastResponse]);

  const lastAssistant = useMemo(
    () => (ceRaw ? lastAssistantTextFromCeResponse(ceRaw) : ''),
    [ceRaw]
  );

  const responseJson = useMemo(
    () => (lastResponse ? JSON.stringify(lastResponse, null, 2) : ''),
    [lastResponse]
  );

  const normalizedSources = useMemo(() => (ceRaw ? extractCeSourcesFromRaw(ceRaw) : []), [ceRaw]);

  const sourcesByIndex = useMemo(
    () => contextEngineSourcesToByIndexMap(normalizedSources),
    [normalizedSources]
  );

  const rawDropboxJson = useMemo(() => (ceRaw ? JSON.stringify(ceRaw, null, 2) : ''), [ceRaw]);

  const parsedCeDraft = useMemo(
    () => (ceRaw ? tryParseStructuredDraftFromCeResponse(ceRaw) : null),
    [ceRaw]
  );

  const rawCeDraftJson = useMemo(() => {
    if (!ceRaw) {
      return '';
    }
    const draft = rawCeDraftFromResponse(ceRaw);
    return draft ? JSON.stringify(draft, null, 2) : '';
  }, [ceRaw]);

  const workstreamIdForAgentParsed = useMemo((): number | undefined => {
    const trimmed = workstreamIdForAgent.trim();
    if (!trimmed) {
      return undefined;
    }
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [workstreamIdForAgent]);

  const parseUserMessageAgentIds = useCallback((): string[] | undefined => {
    const parts = userMessageAgentIdsCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : undefined;
  }, [userMessageAgentIdsCsv]);

  const addAttachmentRow = useCallback(() => {
    setAttachmentRows((rows) => {
      if (rows.length >= ADMIN_CE_MAX_ATTACHMENTS) {
        return rows;
      }
      nextAttachmentId.current += 1;
      return [...rows, {id: nextAttachmentId.current, url: '', title: ''}];
    });
  }, []);

  const removeAttachmentRow = useCallback((id: number) => {
    setAttachmentRows((rows) => rows.filter((r) => r.id !== id));
  }, []);

  const updateAttachmentRow = useCallback(
    (id: number, patch: Partial<{url: string; title: string}>) => {
      setAttachmentRows((rows) => rows.map((r) => (r.id === id ? {...r, ...patch} : r)));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    setErrorText(null);
    setIsSubmitting(true);
    setLastResponse(null);
    setClientRoundTripMs(null);
    setServerElapsedMs(null);

    const seenUrls = new Set<string>();
    const userAttachments: Array<{url: string; title: string}> = [];
    for (const r of attachmentRows) {
      const url = r.url.trim();
      if (!url || seenUrls.has(url)) {
        continue;
      }
      seenUrls.add(url);
      userAttachments.push({url, title: r.title.trim()});
    }

    const payload = buildAdminCeGetResponsePayload(
      message,
      conversationId,
      ADMIN_CONTEXT_ENGINE_SOURCE,
      {
        userAttachments,
        userMessageAgentIds: parseUserMessageAgentIds(),
        modelChoice: modelChoice,
        enableAdvancedCitations,
        enableNativeWriteTools,
        workstreamIdForAgent: workstreamIdForAgentParsed,
        searchParams,
        toolsOrchestration,
        skipPersistence,
        excludeHistory,
        disableDiagnostics,
      }
    );

    const t0 = performance.now();
    roundTripStartRef.current = t0;

    if (skipCelery) {
      try {
        const resp = await fetch(`${OpenAPI.BASE}/api/v1/admin/context-engine/chat/direct`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify(payload),
        });
        const elapsed = Math.round(performance.now() - t0);
        if (!resp.ok) {
          const text = await resp.text();
          setErrorText(`${resp.status}: ${text.slice(0, 500)}`);
          setIsSubmitting(false);
          setClientRoundTripMs(elapsed);
          roundTripStartRef.current = null;
          return;
        }
        const data = await resp.json();
        setLastResponse(data.result ?? data);
        setClientRoundTripMs(elapsed);
        setServerElapsedMs(data.server_elapsed_ms ?? null);
        const nextConv = conversationIdFromCeResponse(data.result ?? data);
        if (nextConv) {
          setConversationId(nextConv);
        }
        setIsSubmitting(false);
        roundTripStartRef.current = null;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setErrorText(msg);
        setIsSubmitting(false);
        setClientRoundTripMs(Math.round(performance.now() - t0));
        roundTripStartRef.current = null;
      }
    } else {
      try {
        await startAdminCePoll(payload);
      } catch (e) {
        const msg =
          e instanceof ApiError
            ? `${e.status}: ${
                typeof e.body === 'object' && e.body && 'detail' in e.body
                  ? String((e.body as {detail?: unknown}).detail)
                  : e.message
              }`
            : e instanceof Error
              ? e.message
              : String(e);
        setErrorText(msg);
        setIsSubmitting(false);
        setClientRoundTripMs(Math.round(performance.now() - t0));
        roundTripStartRef.current = null;
      }
    }
  }, [
    conversationId,
    enableAdvancedCitations,
    enableNativeWriteTools,
    workstreamIdForAgentParsed,
    message,
    modelChoice,
    attachmentRows,
    parseUserMessageAgentIds,
    startAdminCePoll,
    skipCelery,
    searchParams,
    toolsOrchestration,
    skipPersistence,
    excludeHistory,
    disableDiagnostics,
  ]);

  if (!pulseUser) {
    return null;
  }

  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Context engine chat', to: ROUTE_PATHS.ADMIN_CONTEXT_ENGINE},
      ]}
    >
      <Stack gap="24">
        <Title size={24}>Context engine chat</Title>

        <code style={{fontFamily: 'monospace'}}>/2/context_engine/conversations/get_response</code>

        <FormRow>
          <FormLabel htmlFor="admin-ce-message">Message</FormLabel>
          <TextArea
            id="admin-ce-message"
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            placeholder="Ask a question…"
            rows={5}
          />
        </FormRow>

        <FormRow>
          <FormLabel htmlFor="admin-ce-conv">Conversation ID</FormLabel>
          <TextInput
            id="admin-ce-conv"
            value={conversationId}
            onChange={(e) => setConversationId(e.currentTarget.value)}
            placeholder="Filled automatically after each successful response; clear to start a new thread"
          />
        </FormRow>

        <FormRow>
          <FormLabel id="admin-ce-attachments-label">Attachments</FormLabel>
          <Stack gap="8" aria-labelledby="admin-ce-attachments-label">
            {attachmentRows.map((row) => (
              <Box
                key={row.id}
                display="flex"
                style={{flexWrap: 'wrap', gap: 8, alignItems: 'flex-end'}}
              >
                <TextInput
                  aria-label="Attachment URL"
                  value={row.url}
                  onChange={(e) => updateAttachmentRow(row.id, {url: e.currentTarget.value})}
                  placeholder="https://…"
                  style={{minWidth: 220, flex: '1 1 200px'}}
                />
                <TextInput
                  aria-label="Attachment title"
                  value={row.title}
                  onChange={(e) => updateAttachmentRow(row.id, {title: e.currentTarget.value})}
                  placeholder="Optional title"
                  style={{minWidth: 160, flex: '1 1 140px'}}
                />
                <Button type="button" variant="opacity" onClick={() => removeAttachmentRow(row.id)}>
                  Remove
                </Button>
              </Box>
            ))}
            <Button
              type="button"
              variant="opacity"
              disabled={attachmentRows.length >= ADMIN_CE_MAX_ATTACHMENTS}
              onClick={addAttachmentRow}
            >
              Add attachment
            </Button>
          </Stack>
        </FormRow>

        <FormRow>
          <FormLabel htmlFor="admin-ce-msg-agent-ids">User message agent IDs</FormLabel>
          <TextInput
            id="admin-ce-msg-agent-ids"
            value={userMessageAgentIdsCsv}
            onChange={(e) => setUserMessageAgentIdsCsv(e.currentTarget.value)}
            placeholder="companyos_workstream_update"
          />
        </FormRow>

        <FormRow>
          <FormLabel htmlFor="admin-ce-model">Model choice</FormLabel>
          <TextInput
            id="admin-ce-model"
            value={modelChoice}
            onChange={(e) => setModelChoice(e.currentTarget.value)}
            placeholder="e.g. azure:gpt-4.1-nano, openai:gpt-4.1-mini"
          />
        </FormRow>

        <FormRow variant="control">
          <Checkbox
            id="admin-ce-adv-cite"
            checked={enableAdvancedCitations}
            onChange={(e) => setEnableAdvancedCitations(e.target.checked)}
            style={{marginBottom: 8}}
          />
          <FormLabel htmlFor="admin-ce-adv-cite">Enable advanced citations</FormLabel>
        </FormRow>

        <FormRow variant="control">
          <Checkbox
            id="admin-ce-native-draft"
            checked={enableNativeWriteTools}
            onChange={(e) => setEnableNativeWriteTools(e.target.checked)}
            style={{marginBottom: 8}}
          />
          <FormLabel htmlFor="admin-ce-native-draft">
            Enable native write tools (Draft mode)
          </FormLabel>
        </FormRow>
        {enableNativeWriteTools && (
          <Box paddingLeft="24">
            <Text size="xsmall" color="subtle">
              Sets <code style={{fontFamily: 'monospace'}}>user_message.agent_payload</code> to{' '}
              <code style={{fontFamily: 'monospace'}}>
                {`{"enable_native_write_tools":true,...}`}
              </code>{' '}
              so the CompanyOS agent can use CE native draft tools instead of fenced JSON in chat.
            </Text>
          </Box>
        )}

        <FormRow>
          <FormLabel htmlFor="admin-ce-search-params">search_params</FormLabel>
          <select
            id="admin-ce-search-params"
            value={searchParams}
            onChange={(e) => setSearchParams(e.target.value as SearchParamsTag)}
            style={{
              padding: '6px 8px',
              borderRadius: 4,
              border: '1px solid var(--dig-color__border__standard)',
              backgroundColor: 'var(--dig-color__background__standard)',
              color: 'var(--dig-color__text__standard)',
              fontSize: 14,
            }}
          >
            <option value="">(omit — CE default)</option>
            <option value="no_search">no_search — skip retrieval</option>
            <option value="dash_search">dash_search</option>
            <option value="web_search">web_search</option>
          </select>
        </FormRow>

        <FormRow>
          <FormLabel htmlFor="admin-ce-tools-orch">tools_orchestration</FormLabel>
          <select
            id="admin-ce-tools-orch"
            value={toolsOrchestration}
            onChange={(e) => setToolsOrchestration(e.target.value as ToolsOrchestration)}
            style={{
              padding: '6px 8px',
              borderRadius: 4,
              border: '1px solid var(--dig-color__border__standard)',
              backgroundColor: 'var(--dig-color__background__standard)',
              color: 'var(--dig-color__text__standard)',
              fontSize: 14,
            }}
          >
            <option value="">(omit — CE default)</option>
            <option value="default">default (follows GrowthBook)</option>
            <option value="enable">enable orchestration</option>
            <option value="disable">disable orchestration</option>
          </select>
        </FormRow>

        <FormRow variant="control">
          <Checkbox
            id="admin-ce-skip-persist"
            checked={skipPersistence}
            onChange={(e) => setSkipPersistence(e.target.checked)}
            style={{marginBottom: 8}}
          />
          <FormLabel htmlFor="admin-ce-skip-persist">skip_persistence</FormLabel>
        </FormRow>

        <FormRow variant="control">
          <Checkbox
            id="admin-ce-exclude-hist"
            checked={excludeHistory}
            onChange={(e) => setExcludeHistory(e.target.checked)}
            style={{marginBottom: 8}}
          />
          <FormLabel htmlFor="admin-ce-exclude-hist">exclude_history</FormLabel>
        </FormRow>

        <FormRow variant="control">
          <Checkbox
            id="admin-ce-disable-diag"
            checked={disableDiagnostics}
            onChange={(e) => setDisableDiagnostics(e.target.checked)}
            style={{marginBottom: 8}}
          />
          <FormLabel htmlFor="admin-ce-disable-diag">
            Disable diagnostics (debug_enabled=false, no LLM judge, no flair)
          </FormLabel>
        </FormRow>

        <FormRow variant="control">
          <Checkbox
            id="admin-ce-skip-celery"
            checked={skipCelery}
            onChange={(e) => setSkipCelery(e.target.checked)}
            style={{marginBottom: 8}}
          />
          <FormLabel htmlFor="admin-ce-skip-celery">
            Direct call (skip Celery — lower latency, may timeout behind ALB)
          </FormLabel>
        </FormRow>

        <FormRow>
          <FormLabel htmlFor="admin-ce-workstream-id">Workstream ID (agent payload)</FormLabel>
          <TextInput
            id="admin-ce-workstream-id"
            value={workstreamIdForAgent}
            onChange={(e) => setWorkstreamIdForAgent(e.currentTarget.value)}
            placeholder="Optional — recommended for companyos_workstream_update"
            inputMode="numeric"
          />
        </FormRow>

        <Button variant="primary" onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {isSubmitting ? 'Sending…' : 'Send'}
        </Button>

        {errorText && <Text color="error">{errorText}</Text>}

        {clientRoundTripMs !== null && (
          <Stack gap="6">
            <Text isBold>Timings</Text>
            <Text size="xsmall" color="subtle">
              Client round-trip is browser wall time from submit through polling until the result is
              ready (Dropbox RPC runs in a Celery worker, not in the POST handler).
            </Text>
            <Box
              padding="12"
              style={{
                fontFamily: 'monospace',
                fontSize: 13,
                lineHeight: 1.5,
                border: '1px solid var(--dig-color__border__standard)',
                borderRadius: 8,
                backgroundColor: 'var(--dig-color__background__standard)',
              }}
            >
              <div>client_round_trip_ms: {clientRoundTripMs}</div>
              {serverElapsedMs !== null && <div>server_ce_call_ms: {serverElapsedMs}</div>}
            </Box>
          </Stack>
        )}

        {(parsedCeDraft?.body || rawCeDraftJson) && (
          <Stack gap="8">
            <Text isBold>Draft (CE WriteDraft)</Text>
            <Box>
              <Text size="xsmall" color="subtle">
                Populated when the agent uses native write tools (
                <code style={{fontFamily: 'monospace'}}>enable_native_write_tools</code>) and
                returns a top-level <code style={{fontFamily: 'monospace'}}>draft</code> object.
              </Text>
            </Box>
            {parsedCeDraft?.body && (
              <Box
                id="admin-ce-draft-content"
                padding="12"
                style={{
                  minHeight: 80,
                  maxHeight: 360,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 'var(--dig-typography__font-size__small)',
                  lineHeight: 1.45,
                  border: '1px solid var(--dig-color__border__standard)',
                  borderRadius: 8,
                  backgroundColor: 'var(--dig-color__background__standard)',
                }}
              >
                {parsedCeDraft.body}
              </Box>
            )}
            {parsedCeDraft?.status && (
              <Box
                padding="12"
                style={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  lineHeight: 1.5,
                  border: '1px solid var(--dig-color__border__standard)',
                  borderRadius: 8,
                  backgroundColor: 'var(--dig-color__background__standard)',
                }}
              >
                <div>status: {parsedCeDraft.status}</div>
              </Box>
            )}
            {rawCeDraftJson && (
              <TextArea
                id="admin-ce-draft-raw"
                value={rawCeDraftJson}
                readOnly
                rows={8}
                style={{resize: 'none'}}
              />
            )}
          </Stack>
        )}

        {lastAssistant && (
          <Stack gap="8">
            <Text isBold>Response</Text>
            <Box>
              <Text size="xsmall" color="subtle">
                Inline chips show truncated titles; link icon means click opens the URL. Full list:
                sources button. Chips stay disabled when there is no http(s) URL.
              </Text>
            </Box>
            <Box
              id="admin-ce-assistant-preview"
              padding="12"
              style={{
                minHeight: 120,
                maxHeight: 360,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 'var(--dig-typography__font-size__small)',
                lineHeight: 1.45,
                border: '1px solid var(--dig-color__border__standard)',
                borderRadius: 8,
                backgroundColor: 'var(--dig-color__background__standard)',
              }}
            >
              <ContextEngineAssistantTextWithCitationChips
                text={lastAssistant}
                sourcesByIndex={sourcesByIndex}
              />
            </Box>
            {normalizedSources.length > 0 && (
              <ContextEngineSourcesMenuButton sources={normalizedSources} />
            )}
          </Stack>
        )}

        {rawDropboxJson && (
          <Stack gap="8">
            <Text isBold>raw (Dropbox get_response body)</Text>
            <Box>
              <Text size="xsmall" color="subtle">
                Dropbox <code style={{fontFamily: 'monospace'}}>get_response</code> body (same as
                full response JSON below). Citation chips use a client-side extract from this
                object.
              </Text>
            </Box>
            <TextArea
              id="admin-ce-raw-ce"
              value={rawDropboxJson}
              readOnly
              rows={12}
              style={{resize: 'none'}}
            />
          </Stack>
        )}

        {responseJson && (
          <Stack gap="8">
            <Text isBold>Full response JSON</Text>
            <TextArea
              id="admin-ce-raw"
              value={responseJson}
              readOnly
              rows={16}
              style={{resize: 'none'}}
            />
          </Stack>
        )}
      </Stack>
    </Layout.Container>
  );
};
